import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

const Prep = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startPrepTest = async () => {
    setLoading(true);
    setError('');
    
    try {
      const sessionResponse = await apiService.startMockTest();
      const sessionId = sessionResponse.session_id;
      
      console.log('Mock test session started with ID:', sessionId);

      if (!sessionId) {
        throw new Error('No session ID received from server');
      }

      const firstQuestion = await apiService.getNextMockQuestion(sessionId);
      
      console.log('First mock test question received:', firstQuestion);

      if (firstQuestion) {
        navigate('/question', { 
          state: { 
            questions: [firstQuestion], 
            sessionLength: 50,
            difficulty: 'mixed',
            isMockTest: true, 
            sessionId: sessionId,
            testType: 'prep'
          } 
        });
      } else {
        throw new Error('Could not fetch the first question for the prep test');
      }
    } catch (error) {
      console.error('Error starting prep test:', error);
      setError(`Failed to start prep test: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const navigateToPrepProgress = () => {
    navigate('/prep-progress');
  };

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <h1 style={styles.title}>Prep</h1>
            <p style={styles.subtitle}>Premium materials to help you learn and prepare for your exam</p>
          </div>
        </div>
      </div>

      <div style={styles.divider}></div>

      <div style={styles.section}>
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Exam Preparation</h2>
              <button 
                style={styles.progressButton}
                onClick={navigateToPrepProgress}
              >
                <i className="fas fa-chart-line" style={styles.buttonIcon}></i>
                View Progress
              </button>
            </div>
            <p style={styles.cardText}>
              Access comprehensive study materials, practice questions, and learning resources 
              to help you prepare effectively for your exams. Take timed practice tests to 
              simulate real exam conditions and track your progress.
            </p>
            
            {error && (
              <div style={styles.errorAlert}>
                <i className="fas fa-exclamation-circle" style={styles.errorIcon}></i>
                <span style={styles.errorText}>{error}</span>
              </div>
            )}

            <div style={styles.features}>      
              
            </div>

            <div style={styles.testSection}>
              <h3 style={styles.testTitle}>Practice Tests</h3>
              <p style={styles.testDescription}>
                Take a full-length practice test under exam conditions to assess your 
                readiness and identify areas for improvement.
              </p>
              
              <div style={styles.testOptions}>
                <div style={styles.testOption}>
                  <div style={styles.optionHeader}>
                    <i className="fas fa-graduation-cap" style={styles.optionIcon}></i>
                    <h4 style={styles.optionTitle}>Prep Test</h4>
                  </div>
                  <div style={styles.optionDetails}>
                    <p style={styles.optionDescription}>
                      Comprehensive practice test covering all topics with 50 questions
                    </p>
                    <ul style={styles.optionFeatures}>
                      <li>50 questions • 90 minutes</li>
                      <li>Mixed difficulty levels</li>
                      <li>Detailed explanations</li>
                      <li>Performance analytics</li>
                    </ul>
                  </div>
                  <button
                    style={{
                      ...styles.startButton,
                      ...(loading ? styles.startButtonDisabled : {})
                    }}
                    onClick={startPrepTest}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div style={styles.spinner}></div>
                        Starting Test...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-play" style={styles.buttonIcon}></i>
                        Start Prep Test
                      </>
                    )}
                  </button>
                </div>
                
                <div style={styles.testOption}>
                  <div style={styles.optionHeader}>
                    <i className="fas fa-bolt" style={styles.optionIcon}></i>
                    <h4 style={styles.optionTitle}>Quick Quiz</h4>
                  </div>
                  <div style={styles.optionDetails}>
                    <p style={styles.optionDescription}>
                      Short focused quiz for quick practice sessions
                    </p>
                    <ul style={styles.optionFeatures}>
                      <li>20 questions • 30 minutes</li>
                      <li>Topic-specific practice</li>
                      <li>Instant feedback</li>
                      <li>Quick review</li>
                    </ul>
                  </div>
                  <button
                    style={styles.comingSoonButton}
                    disabled
                  >
                    <i className="fas fa-clock" style={styles.buttonIcon}></i>
                    Coming Soon
                  </button>
                </div>
              </div>
            </div>

           
              
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  header: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '30px',
    marginBottom: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0'
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  titleSection: {
    flex: 1
  },
  title: {
    fontSize: '32px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#2c5aa0',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0px',
    lineHeight: '1.2'
  },
  subtitle: {
    fontSize: '16px',
    margin: '0',
    color: '#666666',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.25px',
    lineHeight: '1.5'
  },
  divider: {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '20px 0',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  section: {
    maxWidth: '1200px',
    margin: '0 auto 30px auto'
  },
  content: {
    maxWidth: '1000px',
    margin: '0 auto'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e0e0e0'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#2c5aa0',
    marginBottom: '16px',
    fontFamily: '"Open Sans", sans-serif',
  },
  progressButton: {
    backgroundColor: '#28a745',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: '"Open Sans", sans-serif',
  },
  cardText: {
    fontSize: '16px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Open Sans", sans-serif',
  },
  errorAlert: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorIcon: {
    color: '#dc2626',
    fontSize: '16px',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: '500',
  },
  features: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '40px',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    fontFamily: '"Open Sans", sans-serif',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  featureIcon: {
    color: '#2c5aa0',
    fontSize: '18px',
  },
  testSection: {
    marginBottom: '40px',
    padding: '30px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  testTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#2c5aa0',
    marginBottom: '12px',
    fontFamily: '"Open Sans", sans-serif',
  },
  testDescription: {
    fontSize: '15px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '24px',
    fontFamily: '"Open Sans", sans-serif',
  },
  testOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '24px',
  },
  testOption: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    border: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  optionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  optionIcon: {
    color: '#2c5aa0',
    fontSize: '24px',
  },
  optionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2c5aa0',
    margin: 0,
    fontFamily: '"Open Sans", sans-serif',
  },
  optionDetails: {
    flex: 1,
    marginBottom: '20px',
  },
  optionDescription: {
    fontSize: '14px',
    color: '#666666',
    lineHeight: '1.5',
    marginBottom: '16px',
    fontFamily: '"Open Sans", sans-serif',
  },
  optionFeatures: {
    fontSize: '13px',
    color: '#666666',
    padding: '4px 0',
    fontFamily: '"Open Sans", sans-serif',
    borderBottom: '1px solid #f0f0f0',
  },
  startButton: {
    backgroundColor: '#2c5aa0',
    border: 'none',
    borderRadius: '6px',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: '"Open Sans", sans-serif',
    width: '100%',
  },
  startButtonDisabled: {
    backgroundColor: '#a0a8ac',
    cursor: 'not-allowed',
  },
  comingSoonButton: {
    backgroundColor: '#6b7280',
    border: 'none',
    borderRadius: '6px',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'not-allowed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: '"Open Sans", sans-serif',
    width: '100%',
    opacity: 0.7,
  },
  buttonIcon: {
    fontSize: '16px',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid transparent',
    borderTop: '2px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  statsSection: {
    padding: '30px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  statsTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#2c5aa0',
    marginBottom: '20px',
    fontFamily: '"Open Sans", sans-serif',
    textAlign: 'center',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  statIcon: {
    fontSize: '28px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#333333',
    marginBottom: '4px',
    fontFamily: '"Open Sans", sans-serif',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666666',
    fontWeight: '500',
    fontFamily: '"Open Sans", sans-serif',
  },
};

export default Prep;